import { useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Users } from '../../api-calls';
import {
  Button,
  Cards,
  Grid,
  Inputs as I,
  Icon,
  Typography as T,
} from '../../components';
import validate from '../../validation/schemas/signup';
import { useAuth } from './../../context/auth';
import * as S from './style';

import { navRoutes as R } from '../../constants';

const { Row, Col } = Grid;

const PasswordTipsContent = (
  <div>
    <T.P weight="bold" color="neutralMain">
      A password must contain:
    </T.P>
    <T.P weight="400" color="neutralMain">
      - a minimum of 8 characters
    </T.P>
    <T.P weight="400" color="neutralMain">
      - one capital letter
    </T.P>
    <T.P weight="400" color="neutralMain">
      - one lowercase letter
    </T.P>
    <T.P weight="400" color="neutralMain">
      - one number
    </T.P>
    <T.P weight="400" color="neutralMain">
      - one non alphabetical or numeric character
    </T.P>
  </div>
);

const initialState = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  agreedOnTerms: false,
  httpError: '',
  validationErrs: {},
  loading: false,
};

function reducer(state, newState) {
  return { ...state, ...newState };
}
const cleanEmail = (email) => email.toLowerCase().trim();

const SignUp = () => {
  const submitAttempt = useRef(false);
  const [passwordTipVisible, setPasswordTipVisible] = useState(false);
  const [state, setState] = useReducer(reducer, initialState);
  const {
    firstName,
    lastName,
    email,
    password,
    agreedOnTerms,
    loading,
    validationErrs,
    httpError,
  } = state;
  const navigate = useNavigate();
  const { setUser } = useAuth();

  useEffect(() => {
    if (submitAttempt.current) {
      validateForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName, lastName, email, password, agreedOnTerms]);

  const validateForm = () => {
    try {
      validate({
        email: cleanEmail(email),
        firstName,
        lastName,
        password,
        agreedOnTerms,
      });
      setState({ validationErrs: {} });
      return true;
    } catch (error) {
      if (error.name === 'ValidationError') {
        setState({ validationErrs: error.inner });
      }
      return false;
    }
  };

  const handleSignup = async () => {
    setState({ loading: true });
    const { error, data } = await Users.signup({
      email: cleanEmail(email),
      firstName,
      lastName,
      password,
      agreedOnTerms,
    });

    setState({ loading: false });

    if (error) {
      if (error.statusCode === 409) {
        setState({ validationErrs: { [error.data.field]: error.message } });
      } else {
        setState({ httpError: error.message });
      }
    } else {
      setUser(data);
      navigate(R.ADMIN.DASHBOARD);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    submitAttempt.current = true;

    const isValid = validateForm();
    if (isValid) {
      handleSignup();
    }
  };

  return (
    <S.Form onSubmit={handleSubmit}>
      <T.H1 weight="bold">Sign up</T.H1>
      <Row mt="50px">
        <Col w={[4, 11, 6]}>
          <I.BasicInput
            label="First name"
            placeholder="Type your first name..."
            margins={{ mt: '2', mb: '1' }}
            value={firstName}
            name="firstName"
            autoFocus
            handleChange={(value) => setState({ firstName: value })}
            error={validationErrs.firstName}
          />
        </Col>
      </Row>
      <Row mt="6">
        <Col w={[4, 11, 6]}>
          <I.BasicInput
            label="Last name"
            placeholder="Type your last name..."
            margins={{ mt: '2', mb: '1' }}
            value={lastName}
            name="lastName"
            handleChange={(value) => setState({ lastName: value })}
            error={validationErrs.lastName}
          />
        </Col>
      </Row>
      <Row mt="6">
        <Col w={[4, 11, 6]}>
          <I.BasicInput
            label="Email address"
            placeholder="Type your email..."
            margins={{ mt: '2', mb: '1' }}
            type="email"
            name="email"
            value={email}
            handleChange={(input) => setState({ email: input })}
            error={validationErrs.email}
          />
        </Col>
      </Row>
      <Row mt="6">
        <Col w={[4, 11, 6]}>
          <I.BasicInput
            mb="4"
            label={
              <div style={{ display: 'flex' }}>
                Password{' '}
                <Icon
                  ml="1"
                  width="16"
                  height="16"
                  color="secondaryMain"
                  icon="question"
                  onClick={() => setPasswordTipVisible((_) => !_)}
                />
              </div>
            }
            placeholder="Create a password..."
            margins={{ mt: '2', mb: '1' }}
            type="password"
            value={password}
            handleChange={(input) => setState({ password: input })}
            error={validationErrs.password}
          />

          {passwordTipVisible && (
            <Cards.Tips tips={[PasswordTipsContent]} startingColor={3} />
          )}
        </Col>
      </Row>
      <Row mt="44px">
        <Col w={[4, 11, 6]}>
          <I.Checkbox
            checked={agreedOnTerms}
            handleChange={(v) => setState({ agreedOnTerms: v })}
            ai="flex-start"
            label={
              <T.P>
                I agree to the{' '}
                <T.Link
                  external
                  underline
                  color="neutralMain"
                  weight="semi"
                  to={R.EXTERNAL.TERMS_OF_USE}
                >
                  Terms of use
                </T.Link>
                . By clicking submit I acknowledge the Privacy{' '}
                <T.Link
                  external
                  underline
                  color="neutralMain"
                  weight="semi"
                  to={R.EXTERNAL.PRIVACY_POLICY}
                >
                  Privacy Policy
                </T.Link>
                .
              </T.P>
            }
            error={validationErrs.agreedOnTerms}
          />
        </Col>
      </Row>
      <Row mt="28px">
        <Col w={[4, 11, 6]}>
          {httpError && (
            <T.P mb="2" color="error">
              {httpError}
            </T.P>
          )}
          <Button
            variant="primary"
            loading={loading}
            text="Sign up"
            type="submit"
          />
        </Col>
      </Row>
    </S.Form>
  );
};

export default SignUp;
